/**
 * PrinterForm — Odoo-style full-screen detail view for Network Printers
 *
 * Layout: fixed overlay (right of sidebar, below navbar)
 *   ├─ TopBar   : ← Back | breadcrumb | Save | Discard | Delete
 *   ├─ SmartBar : SmartButtons (model, IP, Ethernet, WiFi, location)
 *   └─ Body (flex row, internal scroll)
 *       ├─ Main  : record title + tabs (Identificación / Conectividad / Ubicación)
 *       └─ Right sidebar : chatter / notes
 *
 * Tabs:
 *   1. Identificación — code, name, model, status, serial_number, purchase_date
 *   2. Conectividad   — mac_address, ip_address, has_ethernet, has_wifi, wifi config
 *   3. Ubicación      — room, floor, location_id, notes
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  WifiIcon,
  MapPinIcon,
  PlusCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import Dialog from './common/Dialog';
import LocationTreeSelect from './common/LocationTreeSelect';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';


const STATUS_OPTS = [
  { value: 'active',       label: 'Activo' },
  { value: 'maintenance',  label: 'Mantenimiento' },
  { value: 'inactive',     label: 'Inactivo' },
];

const WIFI_BANDS = ['2.4GHz', '5GHz', '6GHz', '2.4/5 GHz (Dual)'];
const WIFI_SECURITY = ['WPA2', 'WPA3', 'Abierto'];

function PrinterForm({ printer, companyId, initialMode = 'create', onClose, onSave, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = !!printer?.id;

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isEditing || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ── Form state ──
  const [form, setForm] = useState({
    code: '', name: '', manufacturer: '', model: '', serial_number: '', purchase_date: '',
    mac_address: '', ip_address: '', has_ethernet: true, has_wifi: false,
    wifi_ssid: '', wifi_band: '2.4GHz', wifi_security: 'WPA2',
    room: '', floor: '', location_id: '', notes: '', status: 'active',
  });
  const [isDirty, setIsDirty] = useState(false);

  // ── Catalog state ──
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions]               = useState([]);
  const [addingManufacturer, setAddingManufacturer]   = useState(false);
  const [addingModel, setAddingModel]                 = useState(false);
  const [newManufacturer, setNewManufacturer]         = useState('');
  const [newModel, setNewModel]                       = useState('');

  // ── Data state ──
  const [locations, setLocations] = useState([]);
  const [wifiConfigs, setWifiConfigs] = useState([]);
  const [newWifi, setNewWifi] = useState({ ssid: '', band: '2.4GHz', security: 'WPA2' });
  const [savingWifi, setSavingWifi] = useState(false);

  // ── UI state ──
  const [activeTab, setActiveTab] = useState(isCards ? 'resumen' : 'identificacion');
  const [accessCount, setAccessCount] = useState(0);
  const [apiError, setApiError]   = useState(null);
  const [saving, setSaving]       = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'esta impresora',
    actionLabel: 'pasas a otra impresora o cierras la ficha',
    save: () => handleSubmit(null, { keepEditing: editing }),
  });
  const [savedOk, setSavedOk]    = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  // ── Load data ──
  useEffect(() => {
    if (printer) {
      setForm({
        code:         printer.code         || '',
        name:         printer.name         || '',
        manufacturer: printer.manufacturer || '',
        model:        printer.model        || '',
        serial_number: printer.serial_number || '',
        purchase_date: printer.purchase_date || '',
        mac_address:  printer.mac_address  || '',
        ip_address:   printer.ip_address   || '',
        has_ethernet: printer.has_ethernet ?? true,
        has_wifi:     printer.has_wifi     ?? false,
        wifi_ssid:    printer.wifi_ssid    || '',
        wifi_band:    printer.wifi_band    || '2.4GHz',
        wifi_security: printer.wifi_security || 'WPA2',
        room:         printer.room         || '',
        floor:        printer.floor        || '',
        location_id:  printer.location_id  || '',
        notes:        printer.notes        || '',
        status:       printer.status       || 'active',
      });
    }
  }, [printer]);

  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId)
      .then(r => setLocations(r.data || []))
      .catch(() => {});
  }, [companyId]);

  // ── Load catalogs ──
  const loadCatalogs = useCallback(async () => {
    if (!companyId) return;
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'printer' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'printer' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) { /* ignore */ }
  }, [companyId]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  useEffect(() => {
    if (isEditing && printer?.has_wifi) {
      api.getPrinterWiFiConfigs(printer.id)
        .then(r => setWifiConfigs(r.data || []))
        .catch(() => {});
    }
  }, [isEditing, printer]);

  // ── Handlers ──
  const handleSuggest = async () => {
    try {
      setSuggesting(true);
      const res = await api.suggestName({ type: 'printer', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) { setForm(f => ({ ...f, name: suggested })); setIsDirty(true); }
    } catch {} finally { setSuggesting(false); }
  };

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsDirty(true);
  }, []);

  const handleDiscard = () => {
    if (printer) {
      setForm({
        code:         printer.code         || '',
        name:         printer.name         || '',
        manufacturer: printer.manufacturer || '',
        model:        printer.model        || '',
        serial_number: printer.serial_number || '',
        purchase_date: printer.purchase_date || '',
        mac_address:  printer.mac_address  || '',
        ip_address:   printer.ip_address   || '',
        has_ethernet: printer.has_ethernet ?? true,
        has_wifi:     printer.has_wifi     ?? false,
        wifi_ssid:    printer.wifi_ssid    || '',
        wifi_band:    printer.wifi_band    || '2.4GHz',
        wifi_security: printer.wifi_security || 'WPA2',
        room:         printer.room         || '',
        floor:        printer.floor        || '',
        location_id:  printer.location_id  || '',
        notes:        printer.notes        || '',
        status:       printer.status       || 'active',
      });
    } else {
      onClose();
    }
    setIsDirty(false);
    setApiError(null);
    // Descartar en una ficha ya guardada vuelve a LECTURA (no la cierra).
    if (isEditing) setEditing(false);
  };

  const handleSubmit = async (e, { keepEditing = false } = {}) => {
    if (e) e.preventDefault();
    if (!editing) return false;
    if (!form.code.trim() || !form.name.trim()) {
      setApiError('Código y nombre son obligatorios');
      return false;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload = {
        ...form,
        location_id: form.location_id ? parseInt(form.location_id, 10) : null,
      };
      let creado = null;
      if (isEditing) {
        await api.updatePrinter(printer.id, payload, companyId);
      } else {
        creado = registroGuardado(await api.createPrinter(payload, companyId));
      }
      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado, así la ficha sigue abierta
      // ya con id (y se encienden las zonas que necesitan estar guardado).
      onSave?.(creado);
      // Una ficha existente vuelve a LECTURA tras guardar (salvo «Guardar y continuar»).
      if (isEditing && !keepEditing) setEditing(false);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await api.deletePrinter(printer.id, companyId);
      onClose();
    } catch (err) {
      setApiError(err.message || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWifiConfig = async () => {
    if (!newWifi.ssid.trim()) return;
    setSavingWifi(true);
    try {
      await api.addPrinterWiFiConfig(printer.id, { ...newWifi, is_current: wifiConfigs.length === 0 });
      const r = await api.getPrinterWiFiConfigs(printer.id);
      setWifiConfigs(r.data || []);
      setNewWifi({ ssid: '', band: '2.4GHz', security: 'WPA2' });
    } catch (err) {
      setApiError('Error añadiendo red WiFi');
    } finally {
      setSavingWifi(false);
    }
  };

  // ── Catalog helpers ──
  const saveManufacturerToCatalog = async (nameOverride) => {
    const name = nameOverride || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'printer' }, companyId);
      await loadCatalogs();
    } catch (e) { /* ignore duplicate */ }
  };

  const saveModelToCatalog = async (modelNameOverride) => {
    const modelName = modelNameOverride || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'printer', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) { /* ignore duplicate */ }
  };

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  // ── Computed ──
  const locationName = locations.find(l => String(l.id) === String(form.location_id))?.name || '';
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = form.location_id ? locationPath(mapaUbicaciones, form.location_id) : '';
  const ESTADO_COLOR = { active: '#16a34a', maintenance: '#f59e0b', inactive: '#94a3b8' };
  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => { handleDiscard(); setEditing(false); };

  const tabs = [
    { key: 'identificacion', label: 'Identificación' },
    { key: 'ubicacion',      label: 'Ubicación' },
    { key: 'access',         label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
    { key: 'conectividad',   label: 'Red / IP' },
  ];

  // En tarjetas no hay pestañas «Identificación» ni «Ubicación»: van al Resumen.
  const tabsDef = isCards
    ? [
        { key: 'resumen',      label: 'General' },
        { key: 'conectividad', label: 'Red / IP' },
        ...(isEditing ? [{ key: 'access', label: tabs[2].label }] : []),
      ]
    : tabs;

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon /> Impresoras
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Impresora"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isEditing && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                  <TrashIcon />
                </button>
              )}
            </>
          ) : (
          <>
          <span className={`dv-status-badge ${form.status === 'active' ? 'dv-status-active' : 'dv-status-inactive'}`}>
            ● {STATUS_OPTS.find(s => s.value === form.status)?.label || form.status}
          </span>

          {!editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
              <PencilSquareIcon /> Editar
            </button>
          )}

          {editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon /> Descartar
            </button>
          )}

          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={!editing || saving || (isEditing && !isDirty)}
          >
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>

          {isEditing && (
            <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
              <TrashIcon />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('identificacion')}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 18 }}>🖨️</span>
          <span className="dv-smart-btn-value">{form.model || 'Impresora'}</span>
        </button>

        {form.ip_address && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('conectividad')}>
            <span className="dv-smart-btn-icon">
              <WifiIcon style={{ width: 18, height: 18, color: '#d97706' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.8rem' }}>{form.ip_address}</span>
          </button>
        )}

        {form.has_ethernet && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('conectividad')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 15 }}>🔌</span>
            <span className="dv-smart-btn-value" style={{ color: '#2563eb', fontSize: '0.8rem' }}>Ethernet</span>
          </button>
        )}

        {form.has_wifi && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('conectividad')}>
            <span className="dv-smart-btn-icon">
              <WifiIcon style={{ width: 18, height: 18, color: '#059669' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.8rem' }}>
              {form.wifi_ssid || 'WiFi'}
            </span>
          </button>
        )}

        {locationName && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
            <span className="dv-smart-btn-icon">
              <MapPinIcon style={{ width: 18, height: 18, color: '#dc2626' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#dc2626', fontSize: '0.82rem' }}>{locationName}</span>
          </button>
        )}

        {isEditing && (
          <DocSmartButton elementType="printer" elementId={printer.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        {/* Main panel */}
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<PrinterIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => { setForm(f => ({ ...f, name: v })); setIsDirty(true); }}
                placeholder="Nombre de la impresora…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleCancelEdit}
                onEdit={() => setEditing(true)}
                crumb={[form.manufacturer, form.model, rutaUbicacion || locationName].filter(Boolean).join(' › ')}
                badges={[
                  { text: STATUS_OPTS.find(s => s.value === form.status)?.label || form.status, color: ESTADO_COLOR[form.status] || '#94a3b8' },
                  ...(form.has_ethernet ? [{ text: 'Ethernet' }] : []),
                  ...(form.has_wifi ? [{ text: form.wifi_ssid ? `WiFi: ${form.wifi_ssid}` : 'WiFi' }] : []),
                ]}
                chips={[
                  ...(form.ip_address ? [{ value: form.ip_address, label: 'IP' }] : []),
                  ...(form.mac_address ? [{ value: form.mac_address, label: 'MAC' }] : []),
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setActiveTab('access') }] : []),
                  ...(wifiConfigs.length ? [{ value: wifiConfigs.length, label: 'redes WiFi', onClick: () => setActiveTab('conectividad') }] : []),
                ]}
                extra={printer?.id ? (
                  <DocSmartButton elementType="printer" elementId={printer.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                {/* Record title */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="dv-record-title"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nombre de la impresora…"
                    required
                    style={{ flex: 1 }}
                  />
                  <button type="button" title="Sugerir nombre" onClick={handleSuggest} disabled={suggesting}
                    style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, cursor: suggesting ? 'wait' : 'pointer', color: suggesting ? '#94a3b8' : '#7c3aed', flexShrink: 0 }}>
                    <SparklesIcon style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(tab => (
              <TabButton
                key={tab.key}
                type="button"
                className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset disabled={!editing} className="dv-fieldset">

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && activeTab === 'resumen' && (
            <div className="dv-tab-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>IP</span><b style={{ fontSize: '0.95rem' }}>{form.ip_address || '—'}</b></div>
                <div className="sf2-kpi"><span>Conectividad</span><b style={{ fontSize: '0.95rem' }}>{[form.has_ethernet ? 'Ethernet' : null, form.has_wifi ? 'WiFi' : null].filter(Boolean).join(' + ') || '—'}</b></div>
                <div className="sf2-kpi"><span>Redes WiFi</span><b>{wifiConfigs.length}</b></div>
                <div className="sf2-kpi"><span>Accesos</span><b>{accessCount}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{STATUS_OPTS.find(s => s.value === form.status)?.label || '—'}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código *</label>
                      {editing
                        ? <input value={form.code} onChange={e => handleChange({ target: { name: 'code', value: e.target.value } })} placeholder="PRN-001" />
                        : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => handleChange({ target: { name: 'status', value: e.target.value } })}>
                          {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : <div className="v">{STATUS_OPTS.find(s => s.value === form.status)?.label || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing
                        ? <input value={form.manufacturer} onChange={e => handleChange({ target: { name: 'manufacturer', value: e.target.value } })} placeholder="HP, Brother…" />
                        : <div className="v">{form.manufacturer || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? <input value={form.model} onChange={e => handleChange({ target: { name: 'model', value: e.target.value } })} />
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Nº de serie</label>
                      {editing
                        ? <input value={form.serial_number} onChange={e => handleChange({ target: { name: 'serial_number', value: e.target.value } })} />
                        : <div className="v">{form.serial_number || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fecha de compra</label>
                      {editing
                        ? <input type="date" value={form.purchase_date} onChange={e => handleChange({ target: { name: 'purchase_date', value: e.target.value } })} />
                        : <div className="v">{form.purchase_date || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Ubicación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                          placeholder="— Sin ubicación —"
                        />
                      ) : <div className="v">{rutaUbicacion || locationName || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Planta</label>
                      {editing
                        ? <input value={form.floor} onChange={e => handleChange({ target: { name: 'floor', value: e.target.value } })} placeholder="1ª, Baja…" />
                        : <div className="v">{form.floor || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Sala</label>
                      {editing
                        ? <input value={form.room} onChange={e => handleChange({ target: { name: 'room', value: e.target.value } })} placeholder="Sala principal" />
                        : <div className="v">{form.room || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Red{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Dirección IP</label>
                      {editing
                        ? <input value={form.ip_address} onChange={e => handleChange({ target: { name: 'ip_address', value: e.target.value } })} placeholder="192.168.1.100" />
                        : <div className="v">{form.ip_address || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>MAC</label>
                      {editing
                        ? <input value={form.mac_address} onChange={e => handleChange({ target: { name: 'mac_address', value: e.target.value } })} placeholder="AA:BB:CC:DD:EE:FF" />
                        : <div className="v">{form.mac_address || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Ethernet (RJ45)</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" name="has_ethernet" checked={form.has_ethernet}
                          onChange={e => handleChange({ target: { name: 'has_ethernet', type: 'checkbox', checked: e.target.checked } })} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.has_ethernet ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field">
                      <label>WiFi</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" name="has_wifi" checked={form.has_wifi}
                          onChange={e => handleChange({ target: { name: 'has_wifi', type: 'checkbox', checked: e.target.checked } })} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.has_wifi ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Notas</label>
                      {editing ? (
                        <textarea className="dv-input" rows={3} value={form.notes} onChange={e => handleChange({ target: { name: 'notes', value: e.target.value } })} placeholder="Observaciones…" />
                      ) : <div className="v">{form.notes || '—'}</div>}
                    </div>
                  </div>
                  <span className="sf2-link" role="button" tabIndex={0}
                    onClick={() => setActiveTab('conectividad')}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setActiveTab('conectividad'); } }}
                  >Ver conectividad e IPAM →</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Identificación ── */}
          {activeTab === 'identificacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Datos de la impresora</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código <span style={{ color: '#dc2626' }}>*</span></label>
                    <input className="form-control" name="code" value={form.code} onChange={handleChange} placeholder="IMP-001" required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                      {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Fabricante y modelo</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Fabricante</label>
                    <div className="input-group">
                      <select
                        name="manufacturer"
                        value={form.manufacturer || ''}
                        onChange={e => { handleChange(e); setAddingManufacturer(false); setNewManufacturer(''); }}
                        className="form-select"
                      >
                        <option value="">— Seleccionar —</option>
                        {manufacturerOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setAddingManufacturer(v => !v)} title="Añadir al maestro">
                        Añadir
                      </button>
                    </div>
                    {addingManufacturer && (
                      <div className="input-group mt-2">
                        <input className="form-control" placeholder="Nueva marca" value={newManufacturer} onChange={e => setNewManufacturer(e.target.value)} />
                        <button type="button" className="btn btn-primary" onClick={async () => {
                          if (!newManufacturer.trim()) return;
                          const name = newManufacturer.trim();
                          handleChange({ target: { name: 'manufacturer', value: name } });
                          await saveManufacturerToCatalog(name);
                          setNewManufacturer(''); setAddingManufacturer(false);
                        }}>Guardar</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }}>Cancelar</button>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Modelo</label>
                    <div className="input-group">
                      <select
                        name="model"
                        value={form.model || ''}
                        onChange={e => { handleChange(e); setAddingModel(false); setNewModel(''); }}
                        className="form-select"
                        disabled={filteredModelOptions.length === 0 && !form.manufacturer}
                      >
                        <option value="">— Seleccionar —</option>
                        {filteredModelOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setAddingModel(v => !v)} title="Añadir al maestro">
                        Añadir
                      </button>
                    </div>
                    {addingModel && (
                      <div className="input-group mt-2">
                        <input className="form-control" placeholder="Nuevo modelo" value={newModel} onChange={e => setNewModel(e.target.value)} />
                        <button type="button" className="btn btn-primary" onClick={async () => {
                          if (!newModel.trim()) return;
                          const name = newModel.trim();
                          handleChange({ target: { name: 'model', value: name } });
                          await saveModelToCatalog(name);
                          setNewModel(''); setAddingModel(false);
                        }}>Guardar</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingModel(false); setNewModel(''); }}>Cancelar</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Inventario</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Número de serie</label>
                    <input className="form-control" name="serial_number" value={form.serial_number} onChange={handleChange} placeholder="SN-XXXXXXXX" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Fecha de compra</label>
                    <input className="form-control" type="date" name="purchase_date" value={form.purchase_date} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Conectividad ── */}
          {activeTab === 'conectividad' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Dirección de red</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Dirección IP</label>
                    <input className="form-control" name="ip_address" value={form.ip_address} onChange={handleChange} placeholder="192.168.1.100" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">MAC Address</label>
                    <input className="form-control" name="mac_address" value={form.mac_address} onChange={handleChange} placeholder="AA:BB:CC:DD:EE:FF" />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Interfaces</h3>
                <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      name="has_ethernet"
                      checked={form.has_ethernet}
                      onChange={handleChange}
                      style={{ width: 16, height: 16 }}
                    />
                    🔌 Puerto Ethernet (RJ45)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      name="has_wifi"
                      checked={form.has_wifi}
                      onChange={handleChange}
                      style={{ width: 16, height: 16 }}
                    />
                    📡 WiFi
                  </label>
                </div>

                {form.has_wifi && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">SSID WiFi principal</label>
                      <input className="form-control" name="wifi_ssid" value={form.wifi_ssid} onChange={handleChange} placeholder="MiRedWiFi" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Banda</label>
                      <select className="form-select" name="wifi_band" value={form.wifi_band} onChange={handleChange}>
                        {WIFI_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Seguridad</label>
                      <select className="form-select" name="wifi_security" value={form.wifi_security} onChange={handleChange}>
                        {WIFI_SECURITY.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* WiFi saved configs — only when editing an existing printer with WiFi */}
              {isEditing && form.has_wifi && (
                <div className="dv-section">
                  <h3 className="dv-section-title">Redes WiFi registradas</h3>

                  {wifiConfigs.length > 0 ? (
                    <table className="table table-sm" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th>SSID</th>
                          <th>Banda</th>
                          <th>Seguridad</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wifiConfigs.map(cfg => (
                          <tr key={cfg.id}>
                            <td>{cfg.ssid}</td>
                            <td>{cfg.band}</td>
                            <td>{cfg.security}</td>
                            <td>{cfg.is_current ? <span className="badge bg-success">Actual</span> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No hay redes WiFi registradas.</p>
                  )}

                  <div className="row g-2 align-items-end" style={{ marginTop: '0.75rem' }}>
                    <div className="col-md-4">
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>SSID</label>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Nombre de la red"
                        value={newWifi.ssid}
                        onChange={e => setNewWifi(p => ({ ...p, ssid: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Banda</label>
                      <select className="form-select form-select-sm" value={newWifi.band} onChange={e => setNewWifi(p => ({ ...p, band: e.target.value }))}>
                        {WIFI_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Seguridad</label>
                      <select className="form-select form-select-sm" value={newWifi.security} onChange={e => setNewWifi(p => ({ ...p, security: e.target.value }))}>
                        {WIFI_SECURITY.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="dv-btn dv-btn-primary"
                        style={{ width: '100%' }}
                        onClick={handleAddWifiConfig}
                        disabled={savingWifi || !newWifi.ssid.trim()}
                      >
                        <PlusCircleIcon style={{ width: 16, height: 16 }} />
                        Añadir
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* IPAM section */}
              <div className="dv-section">
                <h3 className="dv-section-title">IPAM — Asignaciones de IP</h3>
                <ElementIpPanel
                  objectType="printer"
                  objectId={printer?.id}
                  companyId={companyId}
                  elementName={printer?.name || form.name}
                />
              </div>
            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {activeTab === 'access' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="printer"
                  objectId={printer?.id}
                  companyId={companyId}
                  elementName={printer?.name || form.name}
                  defaultHost={form.ip_address}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Ubicación ── */}
          {activeTab === 'ubicacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Ubicación física</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Ubicación</label>
                    <LocationTreeSelect
                      locations={locations}
                      value={form.location_id}
                      onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                      placeholder="-- Seleccionar --"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Sala</label>
                    <input className="form-control" name="room" value={form.room} onChange={handleChange} placeholder="Sala de reuniones A" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Piso</label>
                    <input className="form-control" name="floor" value={form.floor} onChange={handleChange} placeholder="Planta 2" />
                  </div>
                </div>
              </div>
            </div>
          )}
          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null)}
          />
          </FormCardsShell>
        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="printer"
          ciId={printer?.id}
          tipoLegible="la impresora"
        />
      </div>

      {/* Diálogo: eliminar impresora */}
      {showDeleteConfirm && (
        <Dialog
          title="Eliminar impresora"
          onClose={() => setShowDeleteConfirm(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar <strong>{form.name || printer?.name}</strong>?{' '}
            Se eliminarán también sus puertos y conexiones. No se puede deshacer.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default PrinterForm;
